import type { Project } from '@hangar/shared';
import { EXECUTION_MODE_LABEL } from '@/components/badges';
import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import { GatedButton } from '@/components/GatedButton';
import { Icon } from '@/components/Icon';
import { ProfilesTable } from '@/features/profiles/ProfilesTable';
import { useProfileActions } from '@/features/profiles/useProfileActions';
import { StepCard, StepNote } from './StepCard';
import { RowsSkeleton, type StepProps } from './StepShared';
import { profilesBlockedNote, profilesBlockedReason, profilesFootnote, profilesNote, profilesTitle, profilesView } from './stepNotesModel';
import type { Setup } from './useSetup';

type ProfileActions = ReturnType<typeof useProfileActions>;

/** A profile that can start a build is missing: what the execution modes have to be. */
function NotRunnableNote({ bitrise }: { bitrise: boolean }) {
  return (
    <StepNote tone="warning" testId="gs-profile-not-runnable">
      No profile can start a build yet: edit one and turn on <strong>{EXECUTION_MODE_LABEL.hangar_runner}</strong> for an Expo app{bitrise ? <>, or <strong>{EXECUTION_MODE_LABEL.provider_api}</strong> for a native app</> : null}. Then Run build can start it.
    </StepNote>
  );
}

function ProfilesList({ setup, project, actions }: { setup: Setup; project: Project; actions: ProfileActions }) {
  return (
    <>
      <ProfilesTable profiles={setup.profiles.data!} project={project} actionsFor={actions.actionsFor} />
      {setup.facts.runnableProfiles === 0 ? <NotRunnableNote bitrise={setup.bitrise} /> : null}
    </>
  );
}

function ProfilesBody({ setup, actions }: { setup: Setup; actions: ProfileActions }) {
  const { project, profiles, state } = setup;
  const view = profilesView(state('profile').status === 'blocked', profiles, profiles.data, Boolean(project));
  if (view === 'blocked') return <StepNote tone="muted">{profilesBlockedNote(state('profile').blockedBy)}</StepNote>;
  if (view === 'loading') return <RowsSkeleton />;
  if (view === 'error') return <ErrorState compact error={profiles.error} onRetry={() => void profiles.refetch()} title="Couldn't load the profiles" />;
  if (view === 'list') return <ProfilesList setup={setup} project={project!} actions={actions} />;
  return (
    <EmptyState
      compact
      testId="gs-profiles-empty"
      icon="tune"
      title={`No build profile for ${setup.app?.name ?? 'the app'} yet`}
      description="A build profile says how the app is built: Android or iOS, which environments, and what each build produces. New profiles are ready for Run build."
    />
  );
}

/** Step 4 · Build profiles of the selected app: create (Hangar's runner by default), edit, duplicate, archive. */
export function ProfilesStep({ setup, index }: StepProps) {
  const { app, project } = setup;
  const actions = useProfileActions({ app, project, accounts: setup.accounts.data });
  const current = setup.progress.next === 'profile';
  return (
    <>
      <StepCard
        state={setup.state('profile')}
        current={current}
        index={index}
        title={profilesTitle(app?.name)}
        summary="How the app is built: Android or iOS, the environments it builds for, and what each build produces."
        collapsedNote={profilesNote(setup.profiles.data, app?.name)}
        footnote={profilesFootnote()}
        footer={
          <GatedButton size="sm" variant={current ? 'default' : 'outline'} data-testid="gs-new-profile" can={actions.can} blockedReason={profilesBlockedReason(Boolean(app), setup.facts.providerAccounts)} onClick={actions.create}>
            <Icon name="add" size={16} />
            New profile
          </GatedButton>
        }
      >
        <ProfilesBody setup={setup} actions={actions} />
      </StepCard>
      {actions.overlays}
    </>
  );
}
